import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Trash2, Heart } from 'lucide-react';
import { useContactRelationships } from '@/hooks/useContactRelationships';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ContactSearchCombobox } from '@/components/common/ContactSearchCombobox';

interface FamilyRelationshipsTabProps {
  contactId: string;
}

export function FamilyRelationshipsTab({ contactId }: FamilyRelationshipsTabProps) {
  const { relationships, isLoading, createRelationship, deleteRelationship } = useContactRelationships(contactId);

  const familyRelationships = relationships.filter(r => r.relationship_kind === 'family');
  const friendRelationships = relationships.filter(r => r.relationship_kind === 'friend');

  const handleDelete = async (relationshipId: string) => {
    try {
      await deleteRelationship(relationshipId);
    } catch (error) {
      // Error already handled in hook
    }
  };

  if (isLoading) {
    return <div className="p-6">Carregando relacionamentos...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Família */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Família</h3>
            <Badge variant="secondary">{familyRelationships.length}</Badge>
          </div>
          <AddRelationshipDialog
            contactId={contactId}
            relationshipKind="family"
            onAdd={createRelationship}
          />
        </div>

        <div className="space-y-3">
          {familyRelationships.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum familiar cadastrado</p>
          ) : (
            familyRelationships.map((rel) => (
              <RelationshipItem
                key={rel.id}
                relationship={rel}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </Card>

      {/* Amigos */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-600" />
            <h3 className="text-lg font-semibold">Amigos</h3>
            <Badge variant="secondary">{friendRelationships.length}</Badge>
          </div>
          <AddRelationshipDialog
            contactId={contactId}
            relationshipKind="friend"
            onAdd={createRelationship}
          />
        </div>

        <div className="space-y-3">
          {friendRelationships.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum amigo cadastrado</p>
          ) : (
            friendRelationships.map((rel) => (
              <RelationshipItem
                key={rel.id}
                relationship={rel}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function RelationshipItem({ relationship, onDelete }: any) {
  const contact = relationship.to_contact;
  const displayName = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || 'Sem nome';

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-sm font-medium text-blue-600">
            {contact?.first_name?.charAt(0) || '?'}
          </span>
        </div>
        <div>
          <p className="font-medium">{displayName}</p>
          {relationship.relationship_type && (
            <Badge variant="outline" className="text-xs mt-1">
              {relationship.relationship_type}
            </Badge>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(relationship.id)}
      >
        <Trash2 className="h-4 w-4 text-red-600" />
      </Button>
    </div>
  );
}

function AddRelationshipDialog({ contactId, relationshipKind, onAdd }: any) {
  const [open, setOpen] = useState(false);
  const [toContactId, setToContactId] = useState('');
  const [relationshipType, setRelationshipType] = useState('');

  const handleSubmit = async () => {
    if (!toContactId) {
      toast.error('Selecione um contato');
      return;
    }

    try {
      await onAdd({
        from_contact_id: contactId,
        to_contact_id: toContactId,
        relationship_kind: relationshipKind,
        relationship_type: relationshipType || undefined,
      });
      setOpen(false);
      setToContactId('');
      setRelationshipType('');
    } catch (error) {
      // Error already handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Adicionar {relationshipKind === 'family' ? 'Familiar' : 'Amigo'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Contato</Label>
            <ContactSearchCombobox
              value={toContactId}
              onValueChange={setToContactId}
              placeholder="Buscar contato pelo nome..."
              excludeContactId={contactId}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Busque e selecione o contato relacionado
            </p>
          </div>
          <div>
            <Label>Tipo de Relacionamento</Label>
            <Select value={relationshipType} onValueChange={setRelationshipType}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {relationshipKind === 'family' ? (
                  <>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="cousin">Cousin</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="close_friend">Close Friend</SelectItem>
                    <SelectItem value="coworker">Coworker</SelectItem>
                    <SelectItem value="neighbor">Neighbor</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSubmit} className="w-full">
            Adicionar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
